<script lang="ts" module>
	export interface Crumb {
		label: string;
		href?: string;
		crumbs?: Crumb[];
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { ChevronRightIcon } from '../../assets/icons/ChevronRightIcon';
	import { Container } from '../Container';
	import { Link } from '../Link';
	import { Txt, type TxtVariant } from '../Txt';

	interface Props {
		crumbs: Crumb[];
		dataCy: string;
		coloredBackground?: boolean | undefined;
		txtVariant?: TxtVariant;
		class?: string;
	}

	let {
		crumbs,
		dataCy,
		coloredBackground = false,
		txtVariant = 'label4',
		...rest
	}: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-breadcrumbs`);
</script>

<!--
	The trail flows as inline text rather than a wrapping flex row, so a long
	crumb wraps mid-label and every wrapped line starts at the container edge,
	level with the first crumb.

	A separator must never start a wrapped line. The browser allows a break
	between text and an inline SVG whatever the surrounding white-space rules,
	so the chevron is glued to the crumb before it through a non-breaking space
	inside a nowrap span: the only text-to-SVG boundary is inside that span.
	That glue has to directly follow the crumb with no whitespace in between,
	or the ordinary space before the nbsp becomes a break opportunity again.
-->
{#snippet separator()}
	<span class="whitespace-nowrap"
		>&nbsp;<ChevronRightIcon
			color="gray-500"
			class={classNames('mr-1 inline-block h-4 min-h-4 w-4 min-w-4 align-middle', {
				'h-3 min-h-3 w-3 min-w-3': txtVariant === 'h5'
			})}
		/></span
	>
{/snippet}

<Container
	class={twMerge(
		classNames('leading-5', {
			'py-4 lg:py-6': coloredBackground,
			'my-4 lg:my-6': !coloredBackground
		}),
		rest.class
	)}
	dataCy={dataCyPrefix}
>
	{#each crumbs as { label, href, crumbs: subCrumbs }, i}
		{@const hasSeparator = i < crumbs.length - 1}
		{#if subCrumbs}
			{#each subCrumbs as subCrumb, j}
				{#if j}
					<Txt component="span" variant={txtVariant} class="text-gray-500">,&nbsp;</Txt>
				{/if}
				<Link href={subCrumb.href} data-cy={`${dataCyPrefix}-crumb`}
					><Txt component="span" variant={txtVariant} class="text-gray-500"
						>{subCrumb.label.trim()}</Txt
					></Link
				>{#if hasSeparator && j === subCrumbs.length - 1}{@render separator()}{/if}
			{/each}
		{:else if href}
			<Link {href} data-cy={`${dataCyPrefix}-crumb`}
				><Txt component="span" variant={txtVariant} class="text-gray-500">{label.trim()}</Txt></Link
			>{#if hasSeparator}{@render separator()}{/if}
		{:else}
			<Txt component="span" variant={txtVariant} dataCy={`${dataCyPrefix}-crumb`}
				>{label.trim()}</Txt
			>{#if hasSeparator}{@render separator()}{/if}
		{/if}
	{/each}
</Container>
